import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useQuery, useLazyQuery } from '@apollo/client';

import {
  ALL_SHARD_MODULES,
  DromoUploader,
  getTranslatedText,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import { AccountClient } from '@nile/graphql';
import { GET_ALL_FIELDS_V2 } from '@nile/banyan/src/Graphql/Mutations/ticket';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';

import {
  getRequesterImportFields,
  shapeOptions,
  getQuery,
  callQuery
} from '../Fields/requesterFields';
import {
  getValidators,
  getFieldType,
  getSelectOptions,
  intlConverter,
  isMultiSelect,
  splitter
} from '../utils/helpers';

const RequesterUploader = ({
  onResults,
  onCancel,
  validationProps = {},
  fields: propFields,
  userToken,
  intl,
  activator
}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);
  const [clientCustomFields, setClientCustomFields] = useState([]);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const [
    getCustomFields,
    { loading: cfLoading, error: cfError, data: customFields }
  ] = useLazyQuery(GET_ALL_FIELDS_V2, {
    variables: {
      input: ALL_SHARD_MODULES?.[isMSPProduct ? 'requester' : 'companyUser']
    },
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    skip: !!validationProps?.customFields,
    onCompleted({ getAllFields }) {
      if (getAllFields) setClientCustomFields(getAllFields);

      const customFieldsToAdd = [];
      getAllFields?.forEach((cf) => {
        if (cf?.fieldCategory === 'CUSTOM') {
          customFieldsToAdd.push({
            label: cf.label,
            key: cf.columnName,
            type: getFieldType(cf),
            selectOptions: getSelectOptions(cf, true),
            validators: getValidators(cf)
          });
        }
      });

      setFields(intlConverter(intl, [...fields, ...customFieldsToAdd]));
    }
  });

  const multiselectFieldValidator = (keyName, values) => {
    const fieldValues = splitter(values);
    const associatedCustomField = clientCustomFields.find(
      ({ columnName }) => columnName === keyName
    );
    let isError = false;
    fieldValues.forEach((value) => {
      if (!isError && !associatedCustomField.options.includes(value))
        isError = true;
    });
    return isError;
  };

  const customFieldValidatorRowHook = ({ row, index }) => {
    const newRecord = { row, index };
    const multiSelectFields = clientCustomFields
      .filter((cf) => isMultiSelect(cf))
      .map(({ columnName }) => columnName);

    multiSelectFields.forEach((field) => {
      if (
        row[field]?.value?.trim() &&
        multiselectFieldValidator(field, row[field].value)
      ) {
        newRecord.row[field].info = [
          {
            message: getTranslatedText(
              intl,
              'dromo.custom.customFields.optionNotFound'
            ),
            level: 'error'
          }
        ];
      } else {
        if (newRecord?.row?.[field]) newRecord.row[field].info = [];
      }
    });

    return newRecord;
  };

  useEffect(() => {
    if (!propFields) {
      setLoading(true);
      let initFields = [...getRequesterImportFields];

      const selectFields = getRequesterImportFields.reduce(
        (acc, field, idx) => {
          if (field.type === 'select')
            acc[idx] = getQuery(field.key)
              ? callQuery(field.key)
              : field.selectOptions;
          return acc;
        },
        {}
      );

      const promises = Object.values(selectFields);
      Promise.all(promises)
        .then((options) => {
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(selectFields)[idx];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields([...intlConverter(intl, initFields)]);
          getCustomFields();
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError(true);
          console.log(e);
        });
    }
  }, []);

  useEffect(() => {
    const { customFields: customFieldsFromProps } = validationProps;
    if (customFieldsFromProps) {
      setClientCustomFields(customFieldsFromProps);
    }
  }, [validationProps]);

  if (loading || cfLoading) return <Loader loading />;

  if (error) return null;

  return (
    <DromoUploader
      settings={{
        importIdentifier: 'REQUESTER',
        title: getTranslatedText(intl, 'dromo.requester.import.title')
      }}
      fields={propFields || fields}
      rowHooks={[(row) => row, customFieldValidatorRowHook]}
      columnHooks={[(column) => column]}
      onResults={(response, metadata) => {
        setResults(response);
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="Requesters" />
            </div>
            <FormattedMessage id="dromo.csv.requester.import" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default injectIntl(RequesterUploader);
